import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheck, Upload, FileText, CheckCircle,
  Lock, Mail, ArrowRight,
  ChevronDown, ChevronUp, Loader2, TrendingUp,
  LogIn, X, User
} from 'lucide-react';

import { BUILD_ID } from '../components/Sidebar';
import TradosPublicHeader from '../components/TradosPublicHeader';
import WPChrome from '../components/WPChrome';
import useCanonical from '../hooks/useCanonical';
import usePageTitle from '../hooks/usePageTitle';
import {
  BRAND_BLUE,
  GRADE_CONFIG, CATEGORY_ORDER, CATEGORY_LABELS,
  DownloadButton, GradeHeroCard, ScoreCard,
  FullReport, IntelAnalysisBox,
  LockedCategories, LockedIntelTeaser,
} from '../components/LOIVerificationComponents';

// Rotating words for the animated tagline
const ROTATING_WORDS = [
  { word: 'Authenticity', color: '#F27A5A' },
  { word: 'Confidence', color: '#F5C842' },
  { word: 'Protection', color: '#F08A4A' },
  { word: 'Transparency', color: '#FFCE47' },
  { word: 'Peace of Mind', color: '#E8B230' },
  { word: 'Trust', color: '#F27A5A' },
  { word: 'Security', color: '#F08A4A' },
  { word: 'Reliability', color: '#F5C842' },
];

const RotatingTagline = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % ROTATING_WORDS.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="text-center py-10 mt-4">
      <div className="flex items-center justify-center gap-3 flex-wrap">
        <span className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-700">
          Trados will bring you
        </span>
        <div className="h-12 sm:h-14 flex items-center overflow-hidden">
          <motion.span
            key={currentIndex}
            initial={{ opacity: 0, y: 25, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -25, scale: 0.95 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight inline-block"
            style={{ color: ROTATING_WORDS[currentIndex].color }}
          >
            {ROTATING_WORDS[currentIndex].word}.
          </motion.span>
        </div>
      </div>
    </div>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PublicLOIVerification = () => {
  const isEmbed = useMemo(() => new URLSearchParams(window.location.search).get('embed') === 'true', []);
  // Embed mode is an iframe widget on other sites, not the canonical page itself
  useCanonical(isEmbed ? null : '/verify-loi');
  usePageTitle(isEmbed ? null : 'Verify LOI');
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [viewState, setViewState] = useState('upload');
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [codeError, setCodeError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const codeInputRefs = useRef([]);

  // Upload progress stages
  const [uploadStage, setUploadStage] = useState(null); // null | { index, label, progress }
  const [debugErrors, setDebugErrors] = useState([]);
  const [debugMode, setDebugMode] = useState(false);

  const [loggedInUser, setLoggedInUser] = useState(null);

  // Check if user is already logged in (token in localStorage)
  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (token && storedUser) {
      try {
        setLoggedInUser(JSON.parse(storedUser));
      } catch (e) {
        // Invalid stored user, ignore
      }
    }
  }, []);

  // Check if debug mode is enabled
  useEffect(() => {
    fetch(`${API_URL}/api/settings/debug-mode`)
      .then(res => res.json())
      .then(data => setDebugMode(data.enabled || false))
      .catch(() => {});
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setLoggedInUser(null);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped && dropped.type === 'application/pdf') {
      setFile(dropped);
      setError('');
    } else {
      setError('Please upload a PDF file');
    }
  }, []);

  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      setFile(selected);
      setError('');
    }
  };

  const UPLOAD_STAGES = [
    { label: 'UPLOADING', duration: 2000 },
    { label: 'LOADING', duration: 2000 },
    { label: 'ANALYSING', duration: 2000 },
    { label: 'REPORTING', duration: 2000 },
    { label: 'SAVING FILE', duration: 2000 },
    { label: 'FINISHING', duration: 500 },
  ];

  const handleAnalyze = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    setResult(null);
    setDebugErrors([]);
    setUploadStage({ index: 0, label: UPLOAD_STAGES[0].label, progress: 0 });

    const startTime = Date.now();
    let apiResult = null;
    let apiError = null;

    // Start the API call immediately
    const apiPromise = (async () => {
      try {
        const formData = new FormData();
        formData.append('file', file);

        if (debugMode) {
          setDebugErrors(prev => [...prev, { time: new Date().toISOString(), type: 'info', msg: `Uploading file: ${file.name} (${(file.size / 1024).toFixed(1)} KB)` }]);
        }

        const response = await fetch(`${API_URL}/api/public/loi-verification/analyze`, {
          method: 'POST',
          body: formData,
        });

        if (debugMode) {
          setDebugErrors(prev => [...prev, { time: new Date().toISOString(), type: response.ok ? 'info' : 'error', msg: `API response: ${response.status} ${response.statusText}` }]);
        }

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          const errMsg = errData.detail || 'Analysis failed';
          if (debugMode) {
            setDebugErrors(prev => [...prev, { time: new Date().toISOString(), type: 'error', msg: `API error detail: ${JSON.stringify(errData)}` }]);
          }
          throw new Error(errMsg);
        }

        const data = await response.json();
        if (debugMode) {
          setDebugErrors(prev => [...prev, {
            time: new Date().toISOString(), type: 'success',
            msg: `Analysis complete — Grade: ${data.summary?.grade}, Score: ${data.summary?.score}%, Checks: ${data.summary?.passed}/${data.summary?.total_checks}, CDN file saved: ${data.verification_id ? 'Yes' : 'No'}`
          }]);
          if (data.intel_analysis) {
            setDebugErrors(prev => [...prev, { time: new Date().toISOString(), type: 'info', msg: `Intel AI status: ${data.intel_analysis.status || 'success'}, Model: ${data.intel_analysis.model_used || 'N/A'}, Red flags: ${data.intel_analysis.red_flag_count ?? data.intel_analysis.sections?.red_flags?.length ?? 0}` }]);
          }
        }
        apiResult = data;
      } catch (err) {
        apiError = err;
        if (debugMode) {
          setDebugErrors(prev => [...prev, { time: new Date().toISOString(), type: 'error', msg: `Request failed: ${err.message}` }]);
        }
      }
    })();

    // Animate through stages with minimum total of 10 seconds
    const totalMinDuration = 10000;
    const stageCount = UPLOAD_STAGES.length;
    const perStageDuration = totalMinDuration / stageCount;

    for (let i = 0; i < stageCount; i++) {
      setUploadStage({ index: i, label: UPLOAD_STAGES[i].label, progress: Math.round(((i + 0.5) / stageCount) * 100) });
      // For the last stage, wait for the API too
      if (i === stageCount - 1) {
        await apiPromise;
        setUploadStage({ index: i, label: UPLOAD_STAGES[i].label, progress: 100 });
        await new Promise(r => setTimeout(r, 500));
      } else {
        await new Promise(r => setTimeout(r, perStageDuration));
      }
    }

    // Ensure minimum total time of 10s has passed
    const elapsed = Date.now() - startTime;
    if (elapsed < totalMinDuration) {
      await new Promise(r => setTimeout(r, totalMinDuration - elapsed));
    }

    setUploadStage(null);
    setUploading(false);

    if (apiError) {
      setError(apiError.message || 'Failed to analyze document');
    } else if (apiResult) {
      setResult(apiResult);
      setViewState('partial');
    }
  };

  const handleRequestReport = async () => {
    if (!email || !result?.verification_id) return;
    setViewState('verifying');
    setError('');

    // Abort controller with 45-second timeout to prevent infinite spinner
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 45000);

    try {
      const url = `${API_URL}/api/public/loi-verification/full-report/${result.verification_id}`;
      if (debugMode) {
        setDebugErrors(prev => [...prev, { time: new Date().toISOString(), type: 'info', msg: `Requesting report: POST ${url} (email: ${email})` }]);
      }
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (debugMode) {
        setDebugErrors(prev => [...prev, { time: new Date().toISOString(), type: response.ok ? 'info' : 'error', msg: `Report API response: ${response.status} ${response.statusText}` }]);
      }
      if (!response.ok) {
        let errorMessage = response.status === 429
          ? 'Rate limit reached. Please try again later or register for an account.'
          : `Failed to send verification email (${response.status}). Please try again.`;
        try {
          // Try JSON first (FastAPI always returns JSON for errors), fall back to text
          const errData = await response.json();
          if (debugMode) {
            setDebugErrors(prev => [...prev, { time: new Date().toISOString(), type: 'error', msg: `Report error detail: ${JSON.stringify(errData)}` }]);
          }
          if (errData.detail) {
            errorMessage = errData.detail;
          }
        } catch (_jsonErr) {
          try {
            const textBody = await response.text();
            if (debugMode) {
              setDebugErrors(prev => [...prev, { time: new Date().toISOString(), type: 'error', msg: `Non-JSON error response: ${textBody.slice(0, 500)}` }]);
            }
          } catch (_) {}
        }
        throw new Error(errorMessage);
      }
      const data = await response.json();
      if (debugMode) {
        setDebugErrors(prev => [...prev, { time: new Date().toISOString(), type: 'success', msg: `Report status: ${data.status}` }]);
      }

      if (data.status === 'already_verified') {
        setResult({ ...result, ...data });
        setViewState('full');
      } else {
        setViewState('code_entry');
      }
    } catch (err) {
      clearTimeout(timeoutId);
      if (debugMode) {
        setDebugErrors(prev => [...prev, { time: new Date().toISOString(), type: 'error', msg: `Report request failed: ${err.name}: ${err.message}` }]);
      }
      if (err.name === 'AbortError') {
        setError('Request timed out. The email service may be slow. Please try again.');
      } else {
        setError(err.message);
      }
      setViewState('partial');
    }
  };

  const handleCodeInput = (index, value) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6);
      const newCode = [...verificationCode];
      for (let i = 0; i < 6; i++) {
        newCode[i] = digits[i] || '';
      }
      setVerificationCode(newCode);
      setCodeError('');
      const focusIdx = Math.min(digits.length, 5);
      codeInputRefs.current[focusIdx]?.focus();
      return;
    }

    const digit = value.replace(/\D/g, '');
    const newCode = [...verificationCode];
    newCode[index] = digit;
    setVerificationCode(newCode);
    setCodeError('');

    if (digit && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyCode = async () => {
    const code = verificationCode.join('');
    if (code.length !== 6) {
      setCodeError('Please enter the full 6-digit code');
      return;
    }

    setVerifying(true);
    setCodeError('');

    try {
      const response = await fetch(
        `${API_URL}/api/public/loi-verification/verify-email/${result.verification_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || 'Invalid code');
      }

      const data = await response.json();
      setResult({ ...result, ...data });
      setViewState('full');
    } catch (err) {
      setCodeError(err.message);
    } finally {
      setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setCodeError('');
    setVerificationCode(['', '', '', '', '', '']);
    try {
      await fetch(
        `${API_URL}/api/public/loi-verification/full-report/${result.verification_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        }
      );
      setCodeError('New code sent! Check your inbox.');
    } catch {
      setCodeError('Failed to resend code');
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setEmail('');
    setError('');
    setViewState('upload');
    setVerificationCode(['', '', '', '', '', '']);
    setCodeError('');
    setUploadStage(null);
    setDebugErrors([]);
  };

  const handleDownloadReport = () => {
    if (!result?.verification_id) return;
    window.open(
      `${API_URL}/api/public/loi-verification/report/${result.verification_id}`,
      '_blank'
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">

      {!isEmbed && <WPChrome type="header" fallback={<TradosPublicHeader loggedInUser={loggedInUser} onLogout={handleLogout} maxWidth="max-w-4xl" />} />}

      <main className="max-w-4xl mx-auto px-4 py-8 lg:py-16">
        {viewState === 'upload' && (
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-3xl sm:text-4xl font-extrabold text-[#2B4B6A] mb-2 tracking-tight leading-[1.1]">
                Verify Your LOI{' '}
                <span className="bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">
                  In Seconds
                </span>
              </h1>
              <p className="text-base text-gray-500 max-w-xl mx-auto mb-8 leading-relaxed">
                Upload any Letter of Intent and our AI-powered engine
                <br className="hidden sm:block" />
                will analyze it for completeness, authenticity, and red flags.
              </p>
            </motion.div>

            {/* Animated gradient border around drop zone */}
            <style>{`
              @property --border-angle {
                syntax: '<angle>';
                initial-value: 0deg;
                inherits: false;
              }
              @keyframes spin-border {
                to { --border-angle: 360deg; }
              }
            `}</style>
            <div
              className="max-w-xl mx-auto rounded-2xl p-[3px] relative overflow-hidden"
              style={{
                background: dragging
                  ? '#60a5fa'
                  : file
                  ? '#4ade80'
                  : 'conic-gradient(from var(--border-angle, 0deg), #F58A5A, #FFD166, #F07A4A, #FFCE47, #E8734A, #FFD166, #F58A5A)',
                animation: (!dragging && !file) ? 'spin-border 3s linear infinite' : 'none',
              }}
            >
              <div
                className={`rounded-[13px] p-12 transition ${
                  uploading ? 'bg-gray-50' : dragging ? 'bg-blue-50 cursor-pointer' : file ? 'bg-green-50 cursor-pointer' : 'bg-white cursor-pointer'
                }`}
                onDrop={!uploading ? handleDrop : undefined}
                onDragOver={!uploading ? (e) => { e.preventDefault(); setDragging(true); } : undefined}
                onDragLeave={!uploading ? () => setDragging(false) : undefined}
                onClick={!uploading ? () => document.getElementById('pdf-upload').click() : undefined}
              >
                <input
                  id="pdf-upload"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                {uploading && uploadStage ? (
                  <div className="text-center">
                    <Loader2 className="w-10 h-10 text-blue-500 mx-auto mb-4 animate-spin" />
                    <motion.p
                      key={uploadStage.label}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="text-lg font-bold text-gray-800 tracking-wider mb-3"
                    >
                      {uploadStage.label}
                    </motion.p>
                    <div className="w-full max-w-xs mx-auto">
                      <div className="w-full h-3 rounded-full bg-gray-200 overflow-hidden">
                        <motion.div
                          className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"
                          initial={{ width: 0 }}
                          animate={{ width: `${uploadStage.progress}%` }}
                          transition={{ duration: 0.8, ease: 'easeInOut' }}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-2">{uploadStage.progress}% complete</p>
                    </div>
                    <p className="text-sm text-gray-500 mt-3">{file?.name}</p>
                  </div>
                ) : file ? (
                  <div className="text-center">
                    <FileText className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <p className="text-gray-900 font-medium">{file.name}</p>
                    <p className="text-gray-400 text-sm mt-1">{(file.size / 1024).toFixed(0)} KB</p>
                  </div>
                ) : (
                  <div className="text-center">
                    <Upload className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-600 font-medium">Drop your PDF here or click to browse</p>
                    <p className="text-gray-400 text-sm mt-1">Maximum 20MB</p>
                  </div>
                )}
              </div>
            </div>

            {error && (
              <p className="text-red-500 text-sm mt-4">{error}</p>
            )}

            {file && !uploading && (
              <div className="mt-6 flex gap-3 justify-center">
                <button
                  onClick={handleReset}
                  className="px-6 py-3 border border-gray-300 text-gray-600 rounded-xl hover:bg-gray-50 text-sm"
                >
                  Change File
                </button>
                <button
                  onClick={handleAnalyze}
                  disabled={uploading}
                  className="px-8 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl hover:from-blue-600 hover:to-cyan-600 font-medium text-sm disabled:opacity-50 flex items-center gap-2"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="w-4 h-4" />
                      Analyze LOI
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Rotating tagline */}
            <RotatingTagline />

            <div className="mt-10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { icon: FileText, title: '40+ Checks', desc: 'Content fields, metadata, edit history, stamps, and forensic analysis', color: '#2B4B6A' },
                    { icon: ShieldCheck, title: 'Fraud Detection', desc: 'Identifies signs of likely manipulation, altered stamps, and modified documents', color: '#F58A5A' },
                    { icon: TrendingUp, title: 'Instant Grade', desc: 'A-E grading system tells you immediately if the LOI is legitimate', color: '#2d7d9a' },
                  ].map((f, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 + i * 0.1 }}
                      className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group"
                    >
                      <div
                        className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300"
                        style={{ backgroundColor: `${f.color}12` }}
                      >
                        <f.icon className="w-7 h-7" style={{ color: f.color }} />
                      </div>
                      <h3 className="text-gray-900 font-bold text-lg mb-2">{f.title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
                    </motion.div>
                  ))}
                </div>
            </div>

            {/* Free LOI badge */}
            <div className="mt-10">
              <div className="inline-flex items-center gap-2 px-5 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold shadow-sm">
                <ShieldCheck className="w-4 h-4" />
                Free LOI Compliance Check
              </div>
            </div>

          </div>
        )}

        {(viewState === 'partial' || viewState === 'verifying' || viewState === 'code_entry') && result && (
          <div>
            <div className="flex flex-col items-center mb-6">
              <img
                src="https://www.trados.co/wp-content/uploads/2026/03/Trados-Trade-Verified-sqaured.jpg"
                alt="Trados Verified"
                style={{ width: 250 }}
              />
            </div>

            <button
              onClick={handleReset}
              className="text-gray-400 hover:text-gray-700 text-sm mb-6 flex items-center gap-1"
            >
              <ArrowRight className="w-4 h-4 rotate-180" /> Analyze another LOI
            </button>

            <GradeHeroCard result={result} />

            <div className="grid grid-cols-3 gap-4 mt-6">
              <ScoreCard label="Level 1 — Must Have" value={result.grading?.level_1} color="red" />
              <ScoreCard label="Level 2 — Important" value={result.grading?.level_2} color="orange" />
              <ScoreCard label="Level 3 — Nice to Have" value={result.grading?.level_3} color="blue" />
            </div>

            {/* Intel AI Analysis Box — locked in partial view (requires email verification) */}
            {result.intel_analysis && (
              <div className="mt-6">
                <LockedIntelTeaser
                  intel={result.intel_analysis}
                  message="Verify your email to unlock the Trados Intel Analysis"
                />
              </div>
            )}

            {/* Show all categories in partial view but locked (non-expandable) */}
            {viewState === 'partial' && result.checks && (
              <LockedCategories result={result} />
            )}

            {viewState === 'partial' && (
              <EmailGate
                email={email}
                setEmail={setEmail}
                onUnlock={handleRequestReport}
                error={error}
              />
            )}

            {viewState === 'verifying' && (
              <VerifyingAnimation />
            )}

            {viewState === 'code_entry' && (
              <CodeEntry
                email={email}
                code={verificationCode}
                onCodeInput={handleCodeInput}
                onCodeKeyDown={handleCodeKeyDown}
                onVerify={handleVerifyCode}
                onResend={handleResendCode}
                verifying={verifying}
                error={codeError}
                codeInputRefs={codeInputRefs}
              />
            )}

            <div className="mt-8 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 text-center">
              <h3 className="text-xl font-bold text-white mb-2">
                Stop wasting time on bad deals.
              </h3>
              <p className="text-white/80 mb-4">
                Join Trados to verify buyers, sellers, and documents. Conduct safer, more efficient business.
              </p>
              <a
                href="/register"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-700 rounded-xl font-medium hover:bg-gray-100 transition"
              >
                Get Started Free <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        )}

        {viewState === 'full' && result && (
          <div>
            <div className="flex flex-col items-center mb-6">
              <img
                src="https://www.trados.co/wp-content/uploads/2026/03/Trados-Trade-Verified-sqaured.jpg"
                alt="Trados Verified"
                style={{ width: 250 }}
              />
            </div>

            <div className="flex items-center justify-between mb-6">
              <button
                onClick={handleReset}
                className="text-gray-400 hover:text-gray-700 text-sm flex items-center gap-1"
              >
                <ArrowRight className="w-4 h-4 rotate-180" /> Analyze another LOI
              </button>
              <DownloadButton onClick={handleDownloadReport} />
            </div>

            {/* User paid status indicator */}
            {result.is_paid_user !== undefined && (
              <div className={`mb-4 px-4 py-2 rounded-lg text-sm font-medium text-center ${
                result.is_paid_user
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-amber-50 text-amber-700 border border-amber-200'
              }`}>
                {result.is_paid_user
                  ? 'Paid Member — Full Intel Access'
                  : 'Free User — Intel preview only (first 30 characters)'}
              </div>
            )}

            <GradeHeroCard result={result} />

            <div className="grid grid-cols-3 gap-4 mt-6">
              <ScoreCard label="Level 1 — Must Have" value={result.grading?.level_1} color="red" />
              <ScoreCard label="Level 2 — Important" value={result.grading?.level_2} color="orange" />
              <ScoreCard label="Level 3 — Nice to Have" value={result.grading?.level_3} color="blue" />
            </div>

            <FullReport result={result} />


            <div className="mt-6 flex flex-col items-center gap-2">
              <DownloadButton onClick={handleDownloadReport} large />
              {result?.verification_id && (
                <p className="text-xs text-gray-400 break-all text-center">
                  {`${API_URL}/api/public/loi-verification/report/${result.verification_id}`}
                </p>
              )}
            </div>

            <div className="mt-8 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 text-center">
              <h3 className="text-xl font-bold text-white mb-2">
                Stop wasting time on bad deals.
              </h3>
              <p className="text-white/80 mb-4">
                Join Trados to verify buyers, sellers, and documents. Conduct safer, more efficient business.
              </p>
              <a
                href="/register"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-700 rounded-xl font-medium hover:bg-gray-100 transition"
              >
                Get Started Free <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        )}
      </main>

      {!isEmbed && <WPChrome type="footer" />}

      {/* Debug Error Panel */}
      {debugMode && debugErrors.length > 0 && (
        <div className="max-w-4xl mx-auto px-4 mb-4">
          <div className="bg-gray-900 rounded-xl border border-gray-700 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
              <span className="text-xs font-bold text-amber-400 tracking-wider">DEBUG LOG</span>
              <button onClick={() => setDebugErrors([])} className="text-xs text-gray-500 hover:text-gray-300">Clear</button>
            </div>
            <div className="p-3 max-h-64 overflow-y-auto space-y-1 font-mono text-xs">
              {debugErrors.map((entry, i) => (
                <div key={i} className={`flex gap-2 ${
                  entry.type === 'error' ? 'text-red-400' : entry.type === 'success' ? 'text-green-400' : 'text-gray-400'
                }`}>
                  <span className="text-gray-600 flex-shrink-0">{entry.time.split('T')[1]?.slice(0, 12)}</span>
                  <span className={`flex-shrink-0 w-5 text-center ${
                    entry.type === 'error' ? 'text-red-500' : entry.type === 'success' ? 'text-green-500' : 'text-blue-400'
                  }`}>
                    {entry.type === 'error' ? '!' : entry.type === 'success' ? '+' : 'i'}
                  </span>
                  <span>{entry.msg}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Build Number */}
      {!isEmbed && (
        <div className="text-center py-2">
          <span className="text-xs text-gray-400 font-mono">Build: {BUILD_ID}</span>
        </div>
      )}

    </div>
  );
};

// ============================================================
// Page-specific Sub-Components (public page only)
// ============================================================

const EmailGate = ({ email, setEmail, onUnlock, error }) => (
  <>
    <style>{`
      @keyframes blink-green {
        0%, 100% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0); border-color: #d1d5db; }
        50% { box-shadow: 0 0 8px 2px rgba(74, 222, 128, 0.4); border-color: #4ade80; }
      }
    `}</style>
    <div
      className="mt-8 rounded-2xl p-[3px] relative overflow-hidden"
      style={{
        background: 'conic-gradient(from var(--border-angle, 0deg), #F58A5A, #FFD166, #F07A4A, #FFCE47, #E8734A, #FFD166, #F58A5A)',
        animation: 'spin-border 3s linear infinite',
      }}
    >
      <div className="bg-white rounded-[13px] p-8 text-center">
        <Lock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <h3 className="text-xl font-bold text-gray-900 mb-2">Unlock and Download Full Analysis Free</h3>
        <p className="text-gray-500 mb-6 max-w-md mx-auto">
          Enter your email to receive the complete verification report with all check details,
          extracted contacts, and forensic findings.
        </p>
        <div className="flex gap-2 max-w-md mx-auto">
          <div className="flex-1 relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onUnlock()}
              placeholder="your@email.com"
              className="w-full pl-10 pr-4 py-3 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400"
              style={{ animation: 'blink-green 1.5s ease-in-out infinite' }}
            />
          </div>
          <button
            onClick={onUnlock}
            disabled={!email}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-medium disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
          >
            <ArrowRight className="w-4 h-4" />
            Get Report
          </button>
        </div>
        {error && (
          <div className="mt-3">
            <p className="text-red-500 text-sm">{error}</p>
            {(error.toLowerCase().includes('limit') || error.toLowerCase().includes('rate') || error.includes('429')) && (
              <p className="text-sm text-gray-600 mt-2">
                <a href="https://www.trados.app/register" className="text-blue-600 font-semibold hover:underline">
                  Create a free account
                </a>{' '}
                or{' '}
                <a href="https://www.trados.app/login" className="text-blue-600 font-semibold hover:underline">
                  log in
                </a>{' '}
                to get unlimited LOI verifications.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  </>
);

const VerifyingAnimation = () => (
  <div className="mt-8 bg-white border border-gray-200 rounded-2xl p-12 text-center shadow-sm">
    {/* Spinning Trados logo animation */}
    <div className="relative w-20 h-20 mx-auto mb-6">
      <div className="absolute inset-0 animate-spin" style={{ animationDuration: '2s' }}>
        <div className="w-full h-full flex flex-col justify-center items-center gap-1">
          <div className="w-8 h-2 rounded-full bg-yellow-400"></div>
          <div className="w-10 h-2 rounded-full bg-orange-400"></div>
          <div className="w-12 h-2 rounded-full bg-yellow-400"></div>
        </div>
      </div>
      {/* Outer glow ring */}
      <div className="absolute inset-[-8px] rounded-full border-2 border-orange-200 animate-pulse"
        style={{ animationDuration: '1.5s' }}
      />
    </div>

    <h3 className="text-xl font-bold text-gray-900 mb-2">Generating Your Report</h3>
    <p className="text-gray-500 max-w-md mx-auto">
      We're preparing your full verification report. Please wait...
    </p>

    <div className="flex justify-center gap-1.5 mt-4">
      {[0, 1, 2].map(i => (
        <div
          key={i}
          className="w-2 h-2 rounded-full bg-blue-400 animate-bounce"
          style={{ animationDelay: `${i * 0.2}s`, animationDuration: '1s' }}
        />
      ))}
    </div>
  </div>
);

const CodeEntry = ({ email, code, onCodeInput, onCodeKeyDown, onVerify, onResend, verifying, error, codeInputRefs }) => (
  <div className="mt-8 bg-white border border-gray-200 rounded-2xl p-8 text-center shadow-sm">
    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-50 flex items-center justify-center">
      <Mail className="w-8 h-8 text-blue-500" />
    </div>

    <h3 className="text-xl font-bold text-gray-900 mb-2">Verify Your Email</h3>
    <p className="text-gray-500 mb-2 max-w-md mx-auto">
      We sent a 6-digit verification code to
    </p>
    <p className="text-gray-900 font-medium mb-6">{email}</p>

    <div className="flex gap-3 justify-center mb-6">
      {code.map((digit, idx) => (
        <input
          key={idx}
          ref={el => codeInputRefs.current[idx] = el}
          type="text"
          inputMode="numeric"
          maxLength={idx === 0 ? 6 : 1}
          value={digit}
          onChange={(e) => onCodeInput(idx, e.target.value)}
          onKeyDown={(e) => onCodeKeyDown(idx, e)}
          className="w-12 h-14 text-center text-2xl font-bold text-gray-900 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      ))}
    </div>

    {error && (
      <p className={`text-sm mb-4 ${error.includes('sent') ? 'text-green-600' : 'text-red-500'}`}>
        {error}
      </p>
    )}

    <button
      onClick={onVerify}
      disabled={verifying || code.join('').length !== 6}
      className="px-8 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-medium disabled:opacity-50 flex items-center gap-2 mx-auto"
    >
      {verifying ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          Verifying...
        </>
      ) : (
        <>
          <CheckCircle className="w-4 h-4" />
          Verify & View Report
        </>
      )}
    </button>

    <button
      onClick={onResend}
      className="text-gray-400 hover:text-gray-600 text-sm mt-4 transition"
    >
      Didn't receive the code? Resend
    </button>
  </div>
);


// ============================================================
// Brand Mark Components (public page only)
// ============================================================

const TradosLogoMark = ({ className = '', size = 'md' }) => {
  const sizes = {
    sm: { pills: 'w-1 h-2.5', text: 'text-sm', sub: 'text-[5px]', gap: 'gap-px' },
    md: { pills: 'w-1.5 h-3', text: 'text-lg', sub: 'text-[6px]', gap: 'gap-0.5' },
    lg: { pills: 'w-2 h-4', text: 'text-2xl', sub: 'text-[8px]', gap: 'gap-0.5' },
  };
  const s = sizes[size] || sizes.md;

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <div className={`flex ${s.gap} mb-1`}>
        <div className={`${s.pills} rounded-full bg-yellow-400`} />
        <div className={`${s.pills} rounded-full bg-yellow-500`} />
        <div className={`${s.pills} rounded-full bg-orange-400`} />
        <div className={`${s.pills} rounded-full bg-orange-500`} />
        <div className={`${s.pills} rounded-full bg-red-400`} />
      </div>
      <span className={`${s.text} font-extrabold text-[#2B4B6A] tracking-[0.15em] leading-none`}>TRADOS</span>
      <span className={`${s.sub} text-gray-400 tracking-[0.25em] leading-none mt-0.5`}>VERIFIED TRADES</span>
    </div>
  );
};

const TradosShieldBadge = () => (
  <div className="relative w-48 flex-shrink-0">
    {/* Shield shape */}
    <svg viewBox="0 0 200 260" className="w-full h-auto drop-shadow-lg">
      <defs>
        <linearGradient id="shieldGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2B4B6A" />
          <stop offset="100%" stopColor="#1a3550" />
        </linearGradient>
        <linearGradient id="innerGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#34607e" />
          <stop offset="100%" stopColor="#1e4060" />
        </linearGradient>
      </defs>
      {/* Outer shield */}
      <path d="M100 8 L188 40 C188 40 192 170 100 248 C8 170 12 40 12 40 Z" fill="url(#shieldGrad)" />
      {/* Inner shield border */}
      <path d="M100 22 L176 50 C176 50 180 165 100 235 C20 165 24 50 24 50 Z" fill="none" stroke="#4a7a9e" strokeWidth="1.5" opacity="0.6" />
      {/* Logo pills at top */}
      <rect x="72" y="42" width="7" height="14" rx="3.5" fill="#F5C842" />
      <rect x="82" y="42" width="7" height="14" rx="3.5" fill="#F5A623" />
      <rect x="92" y="42" width="7" height="14" rx="3.5" fill="#F08A4A" />
      <rect x="102" y="42" width="7" height="14" rx="3.5" fill="#E8734A" />
      <rect x="112" y="42" width="7" height="14" rx="3.5" fill="#D95B3F" />
      {/* TRADOS text */}
      <text x="100" y="78" textAnchor="middle" fill="white" fontWeight="800" fontSize="18" fontFamily="system-ui, sans-serif" letterSpacing="3">TRADOS</text>
      <text x="100" y="90" textAnchor="middle" fill="#8ab4d0" fontSize="7" fontFamily="system-ui, sans-serif" letterSpacing="2">VERIFIED TRADES</text>
      {/* www.trados.co */}
      <text x="100" y="104" textAnchor="middle" fill="#6a9aba" fontSize="7" fontFamily="system-ui, sans-serif">www.trados.co</text>
      {/* Blue banner */}
      <rect x="30" y="114" width="140" height="30" rx="3" fill="#1565C0" />
      <text x="100" y="134" textAnchor="middle" fill="white" fontWeight="800" fontSize="16" fontFamily="system-ui, sans-serif" letterSpacing="3">VERIFIED</text>
      {/* Checkmark circle */}
      <circle cx="100" cy="175" r="26" fill="#2196F3" />
      <circle cx="100" cy="175" r="22" fill="white" />
      <path d="M88 175 L96 183 L113 166" fill="none" stroke="#2196F3" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      {/* VERIFIED ENTITY text */}
      <text x="100" y="218" textAnchor="middle" fill="#F5C842" fontWeight="800" fontSize="13" fontFamily="system-ui, sans-serif" letterSpacing="1">VERIFIED</text>
      <text x="100" y="234" textAnchor="middle" fill="#F5C842" fontWeight="800" fontSize="13" fontFamily="system-ui, sans-serif" letterSpacing="1">ENTITY</text>
    </svg>
  </div>
);

const TradosQRBadge = () => (
  <div className="relative w-52 flex-shrink-0">
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-md">
      {/* Mini Trados logo on top */}
      <div className="flex items-center justify-center gap-2 mb-3">
        <span className="text-sm font-extrabold text-[#2B4B6A] tracking-wider">TRADOS</span>
        <div className="flex gap-px">
          <div className="w-1 h-2 rounded-full bg-yellow-400" />
          <div className="w-1 h-2 rounded-full bg-yellow-500" />
          <div className="w-1 h-2 rounded-full bg-orange-400" />
          <div className="w-1 h-2 rounded-full bg-orange-500" />
          <div className="w-1 h-2 rounded-full bg-red-400" />
        </div>
      </div>
      {/* QR Code with colored corner borders */}
      <div className="relative p-2">
        {/* Colored corner accents */}
        <div className="absolute top-0 left-0 w-6 h-6 border-t-[3px] border-l-[3px] border-red-500 rounded-tl-md" />
        <div className="absolute top-0 right-0 w-6 h-6 border-t-[3px] border-r-[3px] border-orange-400 rounded-tr-md" />
        <div className="absolute bottom-0 left-0 w-6 h-6 border-b-[3px] border-l-[3px] border-blue-500 rounded-bl-md" />
        <div className="absolute bottom-0 right-0 w-6 h-6 border-b-[3px] border-r-[3px] border-green-500 rounded-br-md" />
        {/* QR placeholder - actual QR is generated per-LOI in PDF */}
        <div className="w-full aspect-square bg-gray-100 rounded flex items-center justify-center">
          <div className="text-center">
            <div className="flex gap-px justify-center mb-1">
              <div className="w-1.5 h-3 rounded-full bg-yellow-400" />
              <div className="w-1.5 h-3 rounded-full bg-orange-400" />
              <div className="w-1.5 h-3 rounded-full bg-red-400" />
            </div>
            <span className="text-2xl font-black text-[#2B4B6A]">T</span>
          </div>
        </div>
      </div>
      {/* SCAN ME label */}
      <div className="mt-2 flex justify-center">
        <span className="bg-[#2B4B6A] text-white text-xs font-bold px-4 py-1 rounded tracking-wider">SCAN ME</span>
      </div>
    </div>
  </div>
);

export default PublicLOIVerification;
